import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Palette, Upload, Save, Loader2, Image as ImageIcon, Plus, Trash2 } from "lucide-react";
import { CompanyFacts } from "@/components/CompanyFacts";

export const BRAND_FONTS = ["Inter", "Space Grotesk", "Manrope", "Libre Franklin", "Archivo", "Poppins", "IBM Plex Sans", "Source Serif 4", "Merriweather", "Playfair Display", "Montserrat", "Work Sans"];

function loadFonts(fams) {
  const id = "brand-fonts-preview";
  let l = document.getElementById(id);
  if (!l) { l = document.createElement("link"); l.id = id; l.rel = "stylesheet"; document.head.appendChild(l); }
  l.href = `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${f.replace(/ /g, "+")}:wght@400;600;700`).join("&")}&display=swap`;
}

export default function Branding() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const heroRef = useRef();

  useEffect(() => { api.get("/branding").then((r) => setB(r.data)).catch(() => setB(null)); }, []);
  useEffect(() => { loadFonts(BRAND_FONTS); }, []);

  if (!b) return <div className="p-8 text-sm text-slate-400">Loading branding…</div>;
  const set = (k, v) => setB((x) => ({ ...x, [k]: v }));

  const onLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2 MB.");
    const rd = new FileReader();
    rd.onload = () => set("logo", rd.result);
    rd.readAsDataURL(f);
  };

  const onHero = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Cover photo must be under 4 MB.");
    const rd = new FileReader();
    rd.onload = () => set("hero_photo", rd.result);
    rd.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/branding", b); setB(r.data); toast.success("Branding saved — it applies to every proposal"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="px-6 sm:px-8 py-8 max-w-[1800px] w-full mx-auto">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Palette className="w-5 h-5 text-[#003E87]" />
          <h1 className="font-display text-2xl font-bold tracking-tight text-black">Proposal branding</h1>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#003E87] hover:bg-[#00305F]" data-testid="branding-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save branding
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-6">Your identity on every client-facing proposal — swap the logo, colors and fonts any time. Nothing is hardcoded.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Card className="p-5 space-y-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Logo</div>
            <div className="flex items-center gap-4">
              <div className="w-40 h-20 rounded border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
                {b.logo ? <img src={b.logo} alt="logo" className="max-h-16 max-w-[9rem] object-contain" data-testid="branding-logo-preview" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onLogo} data-testid="branding-logo-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="branding-logo-upload"><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload logo</Button>
                {b.logo && <button className="text-xs text-rose-600 hover:underline text-left" onClick={() => set("logo", null)} data-testid="branding-logo-remove">Remove</button>}
              </div>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Cover site photo <span className="text-slate-300 normal-case">(optional)</span></div>
            <div className="flex items-center gap-4">
              <div className="w-40 h-20 rounded border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
                {b.hero_photo ? <img src={b.hero_photo} alt="cover" className="w-full h-full object-cover" data-testid="branding-hero-preview" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={heroRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onHero} data-testid="branding-hero-input" />
                <Button variant="outline" size="sm" onClick={() => heroRef.current?.click()} data-testid="branding-hero-upload"><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload cover photo</Button>
                {b.hero_photo && <button className="text-xs text-rose-600 hover:underline text-left" onClick={() => set("hero_photo", null)} data-testid="branding-hero-remove">Remove</button>}
              </div>
            </div>
            <p className="text-xs text-slate-400">Appears as a banner on the proposal &amp; execution-plan covers. Wide landscape works best.</p>
          </Card>

          <Card className="p-5 space-y-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Company</div>
            {[["company_name", "Company name"], ["tagline", "Tagline"], ["address", "Address"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["proposal_contact_name", "Proposal contact (name)"], ["proposal_contact_title", "Proposal contact (title)"], ["footer", "Footer / confidentiality line"]].map(([k, lbl]) => (
              <div key={k}>
                <Label className="text-xs text-slate-500">{lbl}</Label>
                <Input value={b[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`branding-${k}`} className="mt-1" />
              </div>
            ))}
          </Card>

          <Card className="p-5 space-y-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Colors & fonts</div>
            <div className="grid grid-cols-3 gap-3">
              {[["primary", "Primary"], ["accent", "Accent"], ["ink", "Text"]].map(([k, lbl]) => (
                <div key={k}>
                  <Label className="text-xs text-slate-500">{lbl}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={b[k] || "#000000"} onChange={(e) => set(k, e.target.value)} data-testid={`branding-color-${k}`} className="w-9 h-9 rounded border border-slate-200 cursor-pointer" />
                    <Input value={b[k] || ""} onChange={(e) => set(k, e.target.value)} className="font-mono text-xs" />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[["font_heading", "Heading font"], ["font_body", "Body font"]].map(([k, lbl]) => (
                <div key={k}>
                  <Label className="text-xs text-slate-500">{lbl}</Label>
                  <select value={b[k]} onChange={(e) => set(k, e.target.value)} data-testid={`branding-${k}`} className="mt-1 w-full text-sm border border-slate-200 rounded px-2 py-2" style={{ fontFamily: b[k] }}>
                    {BRAND_FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </Card>

          {/* Commercial terms, labor burden & weather now live per-project on the bid's Commercial Terms tab. */}
        </div>

        {/* Live cover preview */}
        <div className="lg:sticky lg:top-6 h-fit">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Live cover preview</div>
          <div className="rounded-lg shadow-lg border border-slate-200 bg-white aspect-[8.5/11] p-8 flex flex-col" data-testid="branding-preview" style={{ fontFamily: b.font_body }}>
            <div style={{ borderBottom: `3px solid ${b.primary}`, paddingBottom: 16, marginBottom: 24 }}>
              {b.logo ? <img src={b.logo} alt="" style={{ maxHeight: 48, marginBottom: 10 }} />
                : <div style={{ color: b.primary, fontWeight: 700, letterSpacing: 1, fontSize: 13 }}>{(b.company_name || "").toUpperCase()}</div>}
              {b.tagline && <div style={{ color: b.accent, fontWeight: 700, fontSize: 11, marginTop: 2 }}>{b.tagline}</div>}
            </div>
            <div style={{ fontFamily: b.font_heading, color: b.ink, fontSize: 40, fontWeight: 700, lineHeight: 1.05 }}>PROPOSAL</div>
            <div style={{ color: b.ink, fontSize: 22, marginTop: 6, fontFamily: b.font_heading }}>Sample Project</div>
            <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>372 MWdc · Iron County, Utah</div>
            <div className="mt-auto flex items-end justify-between">
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", color: "#94a3b8" }}>Prepared for</div>
                <div style={{ color: b.ink, fontWeight: 600 }}>Client Name</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", color: "#94a3b8" }}>Firm fixed price</div>
                <div style={{ fontFamily: b.font_heading, color: b.ink, fontSize: 24, fontWeight: 700 }}>$00,000,000</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CompanyFacts />
    </div>
  );
}
